import React from 'react'
import { Mtds, Sample, useTheme } from 'prove-common'

type MapTooltipProps = {
  containerRef: React.RefObject<HTMLDivElement | null>
  data: Sample[]
  onClose: () => void
  onItemClick: (id: string) => void
}

export const MapTooltip = ({
  containerRef,
  data,
  onClose,
  onItemClick
}: MapTooltipProps) => {
  const { isDarkMode } = useTheme()

  // Guard: If hidden, render nothing to save DOM weight (optional, or rely on display: none)
  // But usually keeping it mounted with display:none is safer for OpenLayers overlay logic
  const isVisible = data.length > 0

  return (
    <div
      ref={containerRef}
      role='dialog'
      aria-label='Prøver'
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        display: isVisible ? 'block' : 'none',
        bottom: '50px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        pointerEvents: 'auto',
        filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))'
      }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Mtds.Card
          data-pad='3'
          style={{
            minWidth: '400px',
            maxWidth: '550px',
            width: 'max-content',
            backgroundColor: isDarkMode ? '#1e1e1e' : 'white',
            border: isDarkMode ? '1px solid #444' : '1px solid #e0e0e0',
            borderRadius: '8px'
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
              paddingBottom: '4px',
              borderBottom: isDarkMode ? '1px solid #444' : '1px solid #eee'
            }}
          >
            <strong style={{ fontSize: '14px' }}>{data.length} Prøver</strong>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onClose()
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0 4px',
                color: 'inherit',
                opacity: 0.6
              }}
              aria-label='Close tooltip'
            >
              &times;
            </button>
          </div>

          {/* List */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              maxHeight: '180px',
              overflowY: 'auto',
              justifyContent: 'center'
            }}
          >
            {data.map((s) => (
              <button
                key={s.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onItemClick(s.id)
                }}
                style={{
                  appearance: 'none',
                  border: isDarkMode ? '1px solid #444' : '1px solid #ccc',
                  backgroundColor: isDarkMode ? '#333' : '#f5f5f5',
                  color: isDarkMode ? '#eee' : '#333',
                  borderRadius: '16px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode
                    ? '#054449'
                    : '#E2F1DF'
                  e.currentTarget.style.borderColor = '#054449'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode
                    ? '#333'
                    : '#f5f5f5'
                  e.currentTarget.style.borderColor = isDarkMode
                    ? '#444'
                    : '#ccc'
                }}
              >
                #{s.id}
              </button>
            ))}
          </div>
        </Mtds.Card>
      </div>

      {/* Arrows */}
      <div
        style={{
          position: 'absolute',
          bottom: '-9px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: `10px solid ${isDarkMode ? '#1e1e1e' : 'white'}`,
          zIndex: 2
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderTop: `10px solid ${isDarkMode ? '#444' : '#e0e0e0'}`,
          zIndex: 0
        }}
      />
    </div>
  )
}
